import { useState, useMemo } from 'react';
import Layout from '@/components/feature/Layout';
import ScrollReveal from '@/components/base/ScrollReveal';
import AnimatedStat from '@/components/base/AnimatedStat';
import JsonLd from '@/components/base/JsonLd';
import { useTranslation } from 'react-i18next';

interface ValueItem {
  icon: string;
  title: string;
  desc: string;
}

interface TimelineItem {
  year: string;
  title: string;
  desc: string;
}

interface CertItem {
  icon: string;
  label: string;
  desc: string;
}

export default function APropos() {
  const { t } = useTranslation();
  const [activeYear, setActiveYear] = useState('2006');

  const aboutSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: t('about.pageTitle'),
    description: t('about.pageSubtitle'),
    url: 'https://is-telecom.co/a-propos',
    about: {
      '@type': 'Organization',
      name: 'IS TELECOM',
      foundingDate: '2006',
      description: 'Pionnier des infrastructures de télécommunication en Afrique, partenaire de Huawei, Ericsson et MTN. Plus de 5000 sites de télécommunication déployés dans 6 pays d\'Afrique.',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Cotonou',
        addressLocality: 'Cotonou',
        addressCountry: 'BJ',
      },
    },
  }), [t]);

  const values = t('about.values', { returnObjects: true }) as unknown as ValueItem[];
  const timeline = t('about.timeline', { returnObjects: true }) as unknown as TimelineItem[];
  const certs = t('about.certs', { returnObjects: true }) as unknown as CertItem[];

  return (
    <Layout>
      <JsonLd data={aboutSchema} />
      {/* Hero */}
      <section className="relative py-20 bg-background-950 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20dark%20geometric%20pattern%20with%20subtle%20green%20teal%20accents%2C%20modern%20minimalist%20corporate%20background%20texture%2C%20professional%20dark%20navy%20design%20with%20soft%20light%20shapes%2C%20elegant%20business%20backdrop&width=1920&height=700&seq=about-hero-01&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-16">
          <h1 className="text-4xl md:text-6xl font-bold font-heading text-white mb-4">
            {t('about.pageTitle')}
          </h1>
          <p className="text-lg md:text-xl text-background-300 max-w-3xl mx-auto">
            {t('about.pageSubtitle')}
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <ScrollReveal variant="fade-up">
        <section className="py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center border border-primary-300/60 rounded-full px-4 py-1.5 mb-6">
                  <span className="text-primary-600 text-xs font-semibold uppercase tracking-wider">{t('about.missionBadge')}</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold font-heading text-foreground-950 mb-6 leading-tight">
                  {t('about.missionTitle')}
                </h2>
                <p className="text-lg text-foreground-600 mb-6 leading-relaxed">
                  {t('about.missionDesc1')}
                </p>
                <p className="text-lg text-foreground-600 mb-8 leading-relaxed">
                  {t('about.missionDesc2')}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <AnimatedStat value={50} suffix="+" label={t('about.statCountries')} duration={2500} />
                  <AnimatedStat value={1000} suffix="+" label={t('about.statProjects')} duration={2500} />
                  <AnimatedStat value={200} suffix="+" label={t('about.statExperts')} duration={2500} />
                  <AnimatedStat value={100} suffix="MW+" label={t('about.statCapacity')} duration={2500} />
                </div>
              </div>
              <div className="relative">
                <div className="rounded-3xl overflow-hidden">
                  <img
                    src="https://storage.helloreaddy.io/project_files/dd4db55e-adff-421a-ab07-f57f9542cdee/6a094aa8-005e-4666-9827-2f560fd03e86_compressed_ChatGPT-Image-3-aot-2026-15_36_06.webp"
                    alt="Vue d&apos;ensemble du chantier IS TELECOM"
                    className="w-full h-80 md:h-[500px] object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-lg max-w-xs hidden md:block border border-background-200/70">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                      <i className="ri-award-line text-primary-600 text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <div className="font-bold text-foreground-950">{t('about.certifiedBadge')}</div>
                      <div className="text-sm text-foreground-500">{t('about.certifiedSub')}</div>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary-500 rounded-2xl -z-10 hidden lg:block animate-float-delayed"></div>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Valeurs */}
      <ScrollReveal variant="fade-up" delay={100}>
        <section className="py-24 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center border border-primary-300/60 rounded-full px-4 py-1.5 mb-6">
                <span className="text-primary-600 text-xs font-semibold uppercase tracking-wider">{t('about.valuesBadge')}</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-heading text-foreground-950">
                {t('about.valuesTitle')}
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {values.map((v) => (
                <div key={v.title} className="bg-white rounded-3xl p-8 border border-background-200/70 hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mb-5">
                    <i className={`${v.icon} text-primary-600 text-2xl w-7 h-7 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="text-xl font-bold font-heading text-foreground-950 mb-3">{v.title}</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Timeline */}
      <ScrollReveal variant="fade-up" delay={100}>
        <section className="py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center border border-primary-300/60 rounded-full px-4 py-1.5 mb-6">
                <span className="text-primary-600 text-xs font-semibold uppercase tracking-wider">{t('about.timelineBadge')}</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-heading text-foreground-950">
                {t('about.timelineTitle')}
              </h2>
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
              {/* Years selector */}
              <div className="lg:w-1/3 flex lg:flex-col gap-3 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
                {timeline.map((item) => (
                  <button
                    key={item.year}
                    onClick={() => setActiveYear(item.year)}
                    className={`flex-shrink-0 px-6 py-4 rounded-2xl text-left transition-all cursor-pointer whitespace-nowrap border ${
                      activeYear === item.year
                        ? 'bg-background-950 text-white border-background-950 shadow-md'
                        : 'bg-white text-foreground-700 hover:bg-background-100 border-background-200/70'
                    }`}
                  >
                    <div className="font-bold text-lg font-heading">{item.year}</div>
                    <div className={`text-sm ${activeYear === item.year ? 'text-background-300' : 'text-foreground-400'}`}>
                      {item.title.slice(0, 35)}...
                    </div>
                  </button>
                ))}
              </div>

              {/* Active detail */}
              <div className="lg:w-2/3">
                {timeline.map((item) => (
                  <div
                    key={item.year}
                    className={`bg-white rounded-3xl p-8 lg:p-10 border border-background-200/70 transition-all ${
                      activeYear === item.year ? 'block' : 'hidden'
                    }`}
                  >
                    <div className="text-6xl font-bold font-heading text-primary-100 mb-4">{item.year}</div>
                    <h3 className="text-2xl font-bold font-heading text-foreground-950 mb-4">{item.title}</h3>
                    <p className="text-lg text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Certifications */}
      <ScrollReveal variant="fade-up" delay={100}>
        <section className="py-24 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center border border-primary-300/60 rounded-full px-4 py-1.5 mb-6">
                <span className="text-primary-600 text-xs font-semibold uppercase tracking-wider">{t('about.certBadge')}</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-heading text-foreground-950 mb-4">
                {t('about.certTitle')}
              </h2>
              <p className="text-lg text-foreground-500 max-w-3xl mx-auto">
                {t('about.certDesc')}
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {certs.map((cert) => (
                <div key={cert.label} className="bg-white rounded-3xl p-6 text-center border border-background-200/70 hover:shadow-md transition-shadow">
                  <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <i className={`${cert.icon} text-primary-600 text-2xl w-7 h-7 flex items-center justify-center`}></i>
                  </div>
                  <div className="font-bold text-foreground-950 text-lg">{cert.label}</div>
                  <div className="text-foreground-500 text-sm">{cert.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>
    </Layout>
  );
}